export type ConsentStatus = 'accepted' | 'rejected' | 'pending';

/**
 * Iubenda Cookie Solution JS API injected onto window by Iubenda's script.
 * Full API reference: https://www.iubenda.com/en/help/1205 (section 5.7)
 */
declare global {
  interface Window {
    _iub?: {
      cs?: {
        api?: {
          isConsentGiven: () => boolean;
          getPreferences: () => IubendaCookiePreferences | null;
        };
      };
    };
  }
}

interface IubendaCookiePreferences {
  id?: string;
  timestamp?: string;
  purposes?: Record<string, boolean>;
}

type IubendaApi = NonNullable<
  NonNullable<NonNullable<Window['_iub']>['cs']>['api']
>;

/**
 * Event name dispatched by the GTM Custom HTML tag "Dispatch Cookie Consent Status Event".
 * See: https://tagmanager.google.com/#/container/accounts/6002196687/containers/172717844/workspaces/90/tags
 */
const GTM_IUBENDA_CONSENT_EVENT_NAME = 'iubenda:consent';
const IUBENDA_COOKIE_PREFIX = '_iub_cs-';

const parseCookieValue = (cookie: string) =>
  JSON.parse(decodeURIComponent(cookie.slice(cookie.indexOf('=') + 1)));

/**
 * Reads consent from the _iub_cs-* cookie set by Iubenda on prior visits.
 *
 * Checked before the JS API as the cookie is available immediately on page load,
 * allowing YouTube/Instagram iframes to be displayed without waiting for the
 * Iubenda script to initialise.
 *
 * The cookie value is a URL-encoded JSON object with `consent` (boolean) and `id` fields.
 */
function readIubendaConsentCookie(): ConsentStatus {
  try {
    const iubendaConsentCookie = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith(IUBENDA_COOKIE_PREFIX));

    if (!iubendaConsentCookie) return 'pending';

    const value = parseCookieValue(iubendaConsentCookie);
    if (typeof value?.id === 'undefined') return 'pending';

    return value.consent ? 'accepted' : 'rejected';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[iubenda] Failed to read consent cookie:', error);
    return 'pending';
  }
}

const hasExpressedPreference = (api: IubendaApi): boolean => {
  const prefs = api?.getPreferences?.();
  return !!prefs && typeof prefs.id !== 'undefined';
};

export function readIubendaConsent(): ConsentStatus {
  if (typeof window === 'undefined') return 'pending';

  try {
    const api = window._iub?.cs?.api;

    const cookieStatus = readIubendaConsentCookie();
    if (cookieStatus !== 'pending') return cookieStatus;

    if (api && hasExpressedPreference(api)) {
      return api.isConsentGiven?.() ? 'accepted' : 'rejected';
    }

    return 'pending';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[iubenda] Failed to read consent status:', error);
    return 'pending';
  }
}

/**
 * Subscribes to consent changes dispatched by the GTM Custom HTML tag "Dispatch Cookie Consent Status Event"
 * and returns a cleanup function.
 *
 * The GTM tag dispatches a CustomEvent with { isCookieConsentGranted: boolean }.
 * This observer maps that to a ConsentStatus.
 *
 * Also performs an immediate check via readIubendaConsent() (cookie first, then JS API)
 * to handle consent already stored from a previous visit.
 *
 * See: https://tagmanager.google.com/#/container/accounts/6002196687/containers/32895471/workspaces/41/tags
 */
export function observeIubendaConsent(
  onChange: (status: ConsentStatus) => void
): () => void {
  const handler = (e: CustomEvent<{ isCookieConsentGranted: boolean }>) => {
    onChange(e.detail.isCookieConsentGranted ? 'accepted' : 'rejected');
  };

  window.addEventListener(
    GTM_IUBENDA_CONSENT_EVENT_NAME,
    handler as EventListener
  );

  const existing = readIubendaConsent();
  if (existing !== 'pending') {
    onChange(existing);
  }

  return () => {
    window.removeEventListener(
      GTM_IUBENDA_CONSENT_EVENT_NAME,
      handler as EventListener
    );
  };
}
